/**
 * A short beep per rep, made with Web Audio (no sound file). The audio context starts on the first user action
 * (`unlock`), as browsers require. Muting is the caller's choice: `beep` plays nothing when muted.
 */
let ctx: AudioContext | null = null;

function context(): AudioContext | null {
  if (ctx) return ctx;
  const AC = (globalThis as { AudioContext?: typeof AudioContext; webkitAudioContext?: typeof AudioContext }).AudioContext
    ?? (globalThis as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!AC) return null;
  try { ctx = new AC(); } catch { ctx = null; }
  return ctx;
}

/** Call from a click or tap, so later beeps may play. */
export function unlockAudio(): void {
  const c = context();
  if (c?.state === 'suspended') void c.resume().catch(() => {});
}

/** One beep: `high` for a rep, lower for other cues. */
export function beep(muted: boolean, pitch: 'high' | 'low' = 'high'): void {
  if (muted) return;
  const c = context();
  if (!c) return;
  const osc = c.createOscillator();
  const gain = c.createGain();
  osc.frequency.value = pitch === 'high' ? 1046 : 660;
  osc.type = 'sine';
  const t = c.currentTime;
  gain.gain.setValueAtTime(0.0001, t);
  gain.gain.exponentialRampToValueAtTime(0.4, t + 0.01);
  gain.gain.exponentialRampToValueAtTime(0.0001, t + 0.12);
  osc.connect(gain).connect(c.destination);
  osc.start(t);
  osc.stop(t + 0.13);
}

const MUTE_KEY = 'fitness.sound.muted';
export function readMuted(): boolean {
  try { return globalThis.localStorage?.getItem(MUTE_KEY) === '1'; } catch { return false; }
}
export function writeMuted(muted: boolean): void {
  try { globalThis.localStorage?.setItem(MUTE_KEY, muted ? '1' : '0'); } catch { /* holds for this visit */ }
}
